import type { BiomeKind } from '../generated/BiomeKind';
import type { ChunkCoordJson, MapTileDataJson, RiverSegmentJson } from '../platform/api';
import { chunkKey } from '../platform/store';

function k(x: number, y: number): string {
  return `${x},${y}`;
}

const DIRS: [number, number][] = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

export function isAdjacent(
  cx: number,
  cy: number,
  owned: Set<string>,
  pending: Set<string>,
): boolean {
  for (const [dx, dy] of DIRS) {
    const nk = k(cx + dx, cy + dy);
    if (owned.has(nk) || pending.has(nk)) return true;
  }
  return false;
}

export function isBorderChunk(
  cx: number,
  cy: number,
  allChunks: Set<string>,
): boolean {
  for (const [dx, dy] of DIRS) {
    if (!allChunks.has(k(cx + dx, cy + dy))) return true;
  }
  return false;
}

export function checkContiguityLocal(
  origin: ChunkCoordJson,
  allChunks: Set<string>,
): ChunkCoordJson[] {
  if (allChunks.size === 0) return [];
  const visited = new Set<string>();
  const queue = [k(origin.x, origin.y)];
  visited.add(queue[0]!);

  while (queue.length > 0) {
    const cur = queue.pop()!;
    const [cx, cy] = cur.split(',').map(Number) as [number, number];
    for (const [dx, dy] of DIRS) {
      const nk = k(cx + dx, cy + dy);
      if (allChunks.has(nk) && !visited.has(nk)) {
        visited.add(nk);
        queue.push(nk);
      }
    }
  }

  const disconnected: ChunkCoordJson[] = [];
  for (const ck of allChunks) {
    if (!visited.has(ck)) {
      const [x, y] = ck.split(',').map(Number) as [number, number];
      disconnected.push({ x, y });
    }
  }
  return disconnected;
}

export function candidateSet(
  owned: Set<string>,
  additions: Set<string>,
  removals: Set<string>,
): Set<string> {
  const result = new Set(owned);
  for (const a of additions) result.add(a);
  for (const r of removals) result.delete(r);
  return result;
}

export interface TileLookup {
  get(cx: number, cy: number): MapTileDataJson | null;
}

export function brushPaint(
  cx: number,
  cy: number,
  size: number,
  owned: Set<string>,
  pending: Set<string>,
  allClaimed: Set<string>,
): ChunkCoordJson[] {
  const half = Math.floor(size / 2);
  const footprint = new Set<string>();
  for (let dx = -half; dx < -half + size; dx++) {
    for (let dy = -half; dy < -half + size; dy++) {
      const nk = k(cx + dx, cy + dy);
      if (!owned.has(nk) && !pending.has(nk) && !allClaimed.has(nk)) {
        footprint.add(nk);
      }
    }
  }

  const expandedOwned = new Set(owned);
  for (const p of pending) expandedOwned.add(p);

  const result: ChunkCoordJson[] = [];
  const added = new Set<string>();
  const queue: string[] = [];

  for (const nk of footprint) {
    const [nx, ny] = nk.split(',').map(Number) as [number, number];
    if (isAdjacent(nx, ny, expandedOwned, new Set())) {
      queue.push(nk);
      added.add(nk);
      result.push({ x: nx, y: ny });
    }
  }

  while (queue.length > 0) {
    const cur = queue.shift()!;
    const [qx, qy] = cur.split(',').map(Number) as [number, number];
    for (const [ddx, ddy] of DIRS) {
      const nk = k(qx + ddx, qy + ddy);
      if (footprint.has(nk) && !added.has(nk)) {
        added.add(nk);
        result.push({ x: qx + ddx, y: qy + ddy });
        queue.push(nk);
      }
    }
  }

  return result;
}

export function brushPreview(
  cx: number,
  cy: number,
  size: number,
): ChunkCoordJson[] {
  const half = Math.floor(size / 2);
  const result: ChunkCoordJson[] = [];
  for (let dx = -half; dx < -half + size; dx++) {
    for (let dy = -half; dy < -half + size; dy++) {
      result.push({ x: cx + dx, y: cy + dy });
    }
  }
  return result;
}

const FILL_CAP = 50;

export function fillByBiome(
  startX: number,
  startY: number,
  targetBiome: BiomeKind,
  owned: Set<string>,
  pending: Set<string>,
  allClaimed: Set<string>,
  tileLookup: TileLookup,
): ChunkCoordJson[] {
  const startKey = k(startX, startY);
  if (owned.has(startKey) || pending.has(startKey)) return [];
  if (allClaimed.has(startKey)) return [];
  if (!isAdjacent(startX, startY, owned, pending)) return [];

  const result: ChunkCoordJson[] = [];
  const visited = new Set<string>();
  const queue: ChunkCoordJson[] = [{ x: startX, y: startY }];
  visited.add(startKey);

  const expandedOwned = new Set(owned);
  for (const p of pending) expandedOwned.add(p);

  while (queue.length > 0 && result.length < FILL_CAP) {
    const cur = queue.shift()!;
    const ck = chunkKey(cur);

    if (!owned.has(ck) && !pending.has(ck) && !allClaimed.has(ck)) {
      if (isAdjacent(cur.x, cur.y, expandedOwned, new Set(result.map(chunkKey)))) {
        const tile = tileLookup.get(cur.x, cur.y);
        if (tile && tile.biome === targetBiome) {
          result.push(cur);
          expandedOwned.add(ck);
        } else {
          continue;
        }
      } else {
        continue;
      }
    } else if (!owned.has(ck) && !pending.has(ck)) {
      continue;
    }

    for (const [dx, dy] of DIRS) {
      const nk = k(cur.x + dx, cur.y + dy);
      if (!visited.has(nk)) {
        visited.add(nk);
        queue.push({ x: cur.x + dx, y: cur.y + dy });
      }
    }
  }

  return result;
}

const SNAP_CAP = 30;

export function snapToRiver(
  cx: number,
  cy: number,
  rivers: RiverSegmentJson[],
  owned: Set<string>,
  pending: Set<string>,
  allClaimed: Set<string>,
): ChunkCoordJson[] {
  let bestDist = Infinity;
  let bestRiver: RiverSegmentJson | null = null;
  let bestIdx = 0;

  for (const river of rivers) {
    for (let i = 0; i < river.points.length; i++) {
      const pt = river.points[i]!;
      const dx = pt.x - cx;
      const dy = pt.y - cy;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestDist = d;
        bestRiver = river;
        bestIdx = i;
      }
    }
  }

  if (!bestRiver || bestDist > 9) return [];

  const result: ChunkCoordJson[] = [];
  const added = new Set<string>();
  const expandedOwned = new Set(owned);
  for (const p of pending) expandedOwned.add(p);

  function tryAdd(x: number, y: number): boolean {
    const ck = k(x, y);
    if (added.has(ck)) return true;
    if (allClaimed.has(ck) && !owned.has(ck) && !pending.has(ck)) return false;
    if (owned.has(ck) || pending.has(ck)) return true;
    if (result.length >= SNAP_CAP) return false;
    if (!isAdjacent(x, y, expandedOwned, added)) return true;
    result.push({ x, y });
    added.add(ck);
    expandedOwned.add(ck);
    return true;
  }

  for (let i = bestIdx; i < bestRiver.points.length && result.length < SNAP_CAP; i++) {
    const pt = bestRiver.points[i]!;
    const rx = Math.floor(pt.x);
    const ry = Math.floor(pt.y);
    if (!tryAdd(rx, ry)) break;
    tryAdd(rx - 1, ry);
    tryAdd(rx + 1, ry);
  }
  for (let i = bestIdx - 1; i >= 0 && result.length < SNAP_CAP; i--) {
    const pt = bestRiver.points[i]!;
    const rx = Math.floor(pt.x);
    const ry = Math.floor(pt.y);
    if (!tryAdd(rx, ry)) break;
    tryAdd(rx - 1, ry);
    tryAdd(rx + 1, ry);
  }

  return result;
}


export function fillPreview(
  startX: number,
  startY: number,
  targetBiome: BiomeKind,
  owned: Set<string>,
  pending: Set<string>,
  allClaimed: Set<string>,
  tileLookup: TileLookup,
): ChunkCoordJson[] {
  return fillByBiome(startX, startY, targetBiome, owned, pending, allClaimed, tileLookup);
}

const TILES_PER_PARCEL = 16384; // 128 × 128

const TIERS = [
  { from: 2, to: 8, price: 2.0 },
  { from: 9, to: 24, price: 1.5 },
  { from: 25, to: 64, price: 1.0 },
  { from: 65, to: Infinity, price: 0.75 },
];

export function computeCostPreview(
  currentTileCount: number,
  pendingAddTiles: number,
  pendingRemoveTiles: number,
): { deltaTiles: number; deltaParcels: number; deltaCost: number; breakdown: string } {
  const deltaTiles = pendingAddTiles - pendingRemoveTiles;
  if (deltaTiles === 0) return { deltaTiles: 0, deltaParcels: 0, deltaCost: 0, breakdown: '' };

  const currentExpansion = Math.max(0, currentTileCount - TILES_PER_PARCEL);
  const currentParcels = Math.ceil(currentExpansion / TILES_PER_PARCEL);

  const newTileCount = currentTileCount + deltaTiles;
  const newExpansion = Math.max(0, newTileCount - TILES_PER_PARCEL);
  const newParcels = Math.ceil(newExpansion / TILES_PER_PARCEL);

  const deltaParcels = newParcels - currentParcels;

  function costForRange(from: number, to: number): number {
    let total = 0;
    for (let b = from; b <= to; b++) {
      if (b <= 1) continue;
      const tier = TIERS.find((t) => b >= t.from && b <= t.to);
      total += tier ? tier.price : 0.75;
    }
    return total;
  }

  if (deltaParcels > 0) {
    const cost = costForRange(currentParcels + 1, newParcels);
    const parts: string[] = [];
    let pos = currentParcels + 1;
    for (const tier of TIERS) {
      if (pos > newParcels) break;
      const start = Math.max(pos, tier.from);
      const end = Math.min(newParcels, tier.to);
      if (start > end) continue;
      const count = end - start + 1;
      parts.push(`${count} @ $${tier.price.toFixed(2)}`);
      pos = end + 1;
    }
    return { deltaTiles, deltaParcels, deltaCost: cost, breakdown: parts.join(' + ') };
  } else if (deltaParcels < 0) {
    const saved = costForRange(newParcels + 1, currentParcels);
    return { deltaTiles, deltaParcels, deltaCost: -saved, breakdown: `saves $${saved.toFixed(2)}/mo` };
  } else {
    return { deltaTiles, deltaParcels: 0, deltaCost: 0, breakdown: '' };
  }
}
